// assets/js/components/lazyInitializer.js

import { initTabs } from './ui-components/tabs';
import { initializeAccordion } from './ui-components/accordion';
import { initFeaturesListIndexPage } from './featuresListCardIndexPage';
import { debugLog } from '../debug-mode.js';
// Note: slick-slider is loaded dynamically — see _slickModulePromise below and
// the 'slick-slider' entry in lazyInitializers.

/**
 * Slick chunk promise — set immediately on DOMReady (before runWhenIdle) if
 * the page has any slick slider. This preloads the chunk in parallel with
 * page render, so it's cached before the 1500ms idle delay expires.
 * Pages without sliders never trigger this import.
 */
var _slickModulePromise = null;

/**
 * Registry of eager initializers keyed by the `data-lazy-init` attribute value.
 *
 * Tabs, accordion, and features-list must be ready the instant the page loads, not
 * just when scrolled into view: the browser's native Find-in-Page can reveal a
 * `hidden` panel anywhere in the document (even off-screen, unscrolled) before an
 * IntersectionObserver would ever fire for it. If that happens before the
 * component is initialized, no `beforematch` listener is attached to sync the
 * reveal — for accordion/tabs the on-init logic (auto-opening the first item /
 * activating the first tab) then runs afterward and stomps over whatever the
 * browser just revealed; for features-list, nothing ever adds the `.active` class
 * that drives the hover-content's opacity, so the browser finds and reports the
 * match (e.g. "1/1") but the panel never visually appears. All three modules
 * already ship in the main bundle, so eager init costs zero extra network
 * round-trips — only a few synchronous DOM queries per instance.
 */
const eagerInitializers = {
	'tabs': initTabs,
	'accordion': initializeAccordion,
	'features-list': initFeaturesListIndexPage,
};

/**
 * Registry of lazy initializers keyed by the `data-lazy-init` attribute value.
 *
 * Sliders use dynamic imports so Swiper JS + CSS only load when the slider
 * enters the viewport — keeps Swiper out of the render-blocking CSS bundle.
 */
const lazyInitializers = {
	// Both types share a single "slider-swiper" chunk — identical Swiper config,
	// consolidated in slider-swiper.js to avoid loading duplicate modules.
	'slider-categories': (el) => import(/* webpackChunkName: "slider-swiper" */ './slider-swiper').then(m => m.initSliderCategories(el)),
	'slider-themes':     (el) => import(/* webpackChunkName: "slider-swiper" */ './slider-swiper').then(m => m.initSliderThemes(el)),
	'slider-video-reviews': (el) => import(/* webpackChunkName: "slider-video-reviews" */ './slider-video-reviews').then(m => m.initSliderVideoReviews(el)),
	// Chunk is preloaded early (see initLazyComponents) — by init time it's cached.
	'slick-slider': function(el) {
		return (_slickModulePromise || import(/* webpackChunkName: "slick-slider" */ './ui-components/slick-slider')).then(function(m) {
			m.initSlickSlider(el);
		});
	},

	// 'support-hero': initializeSupportHeroAnimation,
	// add more initializers as needed
};

/**
 * Finds all elements whose `data-lazy-init` type is in `eagerInitializers` and
 * initializes them immediately, synchronously — see the registry comment above.
 */
export function initEagerComponents() {
	Object.keys(eagerInitializers).forEach(initType => {
		document.querySelectorAll(`[data-lazy-init="${initType}"]`).forEach(element => {
			eagerInitializers[initType](element);
		});
	});
}

/**
 * Finds all elements with a `data-lazy-init` type in `lazyInitializers` and sets
 * up an IntersectionObserver to initialize each one the first time it approaches
 * the viewport.
 *
 * Each element is only initialized once — the observer is disconnected from it
 * immediately after the initializer runs.
 * Types that are "cosmetic only" (not critical for page functionality)
 * We can skip them for:
 * - prefers-reduced-motion
 * - Save-Data
 * - touch devices / no fine pointer (no meaningful hover to drive the effect)
 */
const cosmeticTypes = {
};

/**
 * Check if we should disable cosmetic effects.
 * (Helps perf + better Lighthouse + accessibility)
 */
function shouldDisableCosmetics() {
	// prefers-reduced-motion
	if (window.matchMedia) {
		try {
			if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return true;
		} catch (e) {}
	}

	// Save-Data
	if (navigator && navigator.connection) {
		try {
			if (navigator.connection.saveData) return true;
		} catch (e) {}
	}

	// Touch / no fine pointer — these are hover-driven glow effects, dead weight
	// on mobile.
	if (window.matchMedia) {
		try {
			if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return true;
		} catch (e) {}
	}

	return false;
}

/**
 * Runs a callback when browser is idle (best-effort).
 * Helps move non-critical work away from initial render / TBT.
 */
function runWhenIdle(cb, timeout) {
	var t = (typeof timeout === 'number') ? timeout : 1200;

	if (window.requestIdleCallback) {
		window.requestIdleCallback(function () { cb(); }, { timeout: t });
	} else {
		// fallback
		setTimeout(cb, 1);
	}
}

/**
 * Safe DOM ready helper.
 * (So we don't do heavy DOM queries before DOM is ready)
 */
function onDOMReady(fn) {
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', fn);
	} else {
		fn();
	}
}

/**
 * Finds all elements with a `data-lazy-init` type in `lazyInitializers` and
 * configures IntersectionObserver for lazy initialization when they become
 * visible in the viewport.
 */
export function initLazyComponents() {
	onDOMReady(function () {
		// Preload the slick chunk immediately on DOMReady — non-blocking, fire-and-forget.
		// By the time runWhenIdle fires (~1500ms), the chunk is cached and ready.
		// Pages without slick sliders never trigger this import.
		if (document.querySelector('[data-lazy-init="slick-slider"]')) {
			_slickModulePromise = import(/* webpackChunkName: "slick-slider" */ './ui-components/slick-slider');
		}

		// Optional: delay setup until idle to reduce impact on initial paint / TBT.
		// If you want it to run immediately after DOMReady, remove runWhenIdle wrapper.
		runWhenIdle(function () {
			var selector = Object.keys(lazyInitializers).map(function (type) { return '[data-lazy-init="' + type + '"]'; }).join(',');
			var lazyElements = document.querySelectorAll(selector);

			if (!lazyElements.length) {
				return; // Do nothing if there are no elements to initialize
			}

			var disableCosmetics = shouldDisableCosmetics();

			var observerOptions = {
				root: null,          // viewport
				rootMargin: '100px', // trigger 100px before the element enters the viewport
				threshold: 0         // fire as soon as any pixel is visible
			};

			// Guard: if IntersectionObserver is not supported, fallback to immediate init
			if (!('IntersectionObserver' in window)) {
				for (var i = 0; i < lazyElements.length; i++) {
					initElement(lazyElements[i], disableCosmetics);
				}
				return;
			}

			var initializedCount = 0;
			var total = lazyElements.length;

			var lazyObserver = new IntersectionObserver(function (entries, observer) {
				for (var i = 0; i < entries.length; i++) {
					var entry = entries[i];
					if (!entry.isIntersecting) continue;

					var elementToInit = entry.target;

					// Stop observing this element first (prevents double init in edge cases)
					observer.unobserve(elementToInit);

					initElement(elementToInit, disableCosmetics);

					initializedCount++;
					if (initializedCount >= total) {
						observer.disconnect(); // cleanup
					}
				}
			}, observerOptions);

			// Start observing all matched elements
			for (var j = 0; j < lazyElements.length; j++) {
				lazyObserver.observe(lazyElements[j]);
			}
		}, 1500);
	});
}

/**
 * Initializes a single element by its data-lazy-init type.
 * Wrapped in try/catch so one broken initializer doesn't break others.
 */
function initElement(elementToInit, disableCosmetics) {
	if (!elementToInit) return;

	var initType = elementToInit.dataset ? elementToInit.dataset.lazyInit : null;
	if (!initType) return;

	// Skip cosmetic-only effects when user asked to reduce motion / save data
	if (disableCosmetics && cosmeticTypes[initType]) {
		debugLog('Skipping cosmetic initializer due to reduced motion / save-data / touch device:', initType, elementToInit);
		return;
	}

	// Check if initializer exists
	if (typeof lazyInitializers[initType] === 'function') {
		try {
			debugLog('Lazy initializing:', initType, elementToInit);
			lazyInitializers[initType](elementToInit);
		} catch (e) {
			console.warn('Lazy initializer failed for type:', initType, e);
		}
	} else {
		console.warn('No lazy initializer found for type: "' + initType + '"');
	}
}
